import { formatDistanceToNow } from "date-fns";

import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

import type {
    SkillEditorConflictCase,
    SkillEditorFieldConflict,
} from "../../skill-editor-save";
import type { SkillEditorSaveConflictView } from "../useSkillEditor";

export const CONFLICT_FENCE_ID = "skill-editor-conflict-fence";

export interface SaveConflictDialogProps {
    conflict: SkillEditorSaveConflictView | null;
    isResolving?: boolean;
    onReloadConflicts: () => void;
    onKeepLocal: (remoteToken: string) => void;
    onKeepEditing: () => void;
}

const CASE_HEADINGS: Record<SkillEditorConflictCase["kind"], string> = {
    "overlapping-edits": "Changed in both places",
    "remote-edit-against-local-delete":
        "You deleted these; they changed on the server",
    "local-edit-against-remote-delete":
        "You edited these; they were deleted on the server",
    "concurrent-filename-or-path-collision":
        "Name collision will delete a server file",
};

const describeField = (field: SkillEditorFieldConflict) =>
    field.scope === "skill"
        ? field.fieldLabel
        : `${field.fileName} - ${field.fieldLabel}`;

interface ConflictCaseItem {
    readonly key: string;
    readonly label: string;
}

const caseItems = (
    conflictCase: SkillEditorConflictCase
): readonly ConflictCaseItem[] => {
    switch (conflictCase.kind) {
        case "overlapping-edits":
            return conflictCase.fields.map((field) => ({
                key:
                    field.scope === "skill"
                        ? `skill:${field.field}`
                        : `file:${field.serverId}:${field.field}`,
                label: describeField(field),
            }));
        case "remote-edit-against-local-delete":
        case "local-edit-against-remote-delete":
            return conflictCase.files.map(({ serverId, fileName }) => ({
                key: `file:${serverId}`,
                label: fileName,
            }));
        case "concurrent-filename-or-path-collision":
            return conflictCase.collisions.map(
                ({
                    localClientKey,
                    localFileName,
                    remoteServerId,
                    remoteFileName,
                }) => ({
                    key: `${localClientKey}:${remoteServerId}`,
                    label: `${localFileName} will replace server file ${remoteFileName}; ${remoteFileName} will be deleted`,
                })
            );
    }
};

const since = (timestamp: string) =>
    formatDistanceToNow(new Date(timestamp), { addSuffix: true });

export function SaveConflictDialog({
    conflict,
    isResolving = false,
    onReloadConflicts,
    onKeepLocal,
    onKeepEditing,
}: SaveConflictDialogProps) {
    if (!conflict) return null;
    const deletesServerFile = conflict.cases.some(
        ({ kind }) => kind === "concurrent-filename-or-path-collision"
    );

    return (
        <Dialog
            open
            onOpenChange={(next) => {
                if (!next && !isResolving) onKeepEditing();
            }}
        >
            <DialogContent className="sm:max-w-lg" showCloseButton={false}>
                <DialogHeader>
                    <DialogTitle>Resolve conflicting changes</DialogTitle>
                    <DialogDescription>
                        {conflict.supersededAcknowledgment
                            ? "The server changed again while you were deciding, so your earlier choice no longer applies. Nothing has been saved yet. Here is what changed now."
                            : "Someone else changed this skill while you were editing. Nothing has been saved yet."}
                    </DialogDescription>
                </DialogHeader>

                {conflict.cases.map((conflictCase) => (
                    <section key={conflictCase.kind} className="space-y-1">
                        <h3 className="text-sm font-medium">
                            {CASE_HEADINGS[conflictCase.kind]}
                        </h3>
                        <ul className="text-muted-foreground list-disc pl-5 text-sm">
                            {caseItems(conflictCase).map((item) => (
                                <li key={item.key}>{item.label}</li>
                            ))}
                        </ul>
                    </section>
                ))}

                <div
                    id={CONFLICT_FENCE_ID}
                    className="text-muted-foreground border-t pt-3 text-xs"
                >
                    <p>
                        Server version last changed{" "}
                        <time dateTime={conflict.fence.skillUpdatedAt}>
                            {since(conflict.fence.skillUpdatedAt)}
                        </time>
                    </p>
                    {conflict.fence.files.map((file) => (
                        <p key={file.serverId}>
                            {file.fileName} last changed{" "}
                            <time dateTime={file.updatedAt}>
                                {since(file.updatedAt)}
                            </time>
                        </p>
                    ))}
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onKeepEditing}
                        disabled={isResolving}
                    >
                        Keep editing
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onReloadConflicts}
                        disabled={isResolving}
                    >
                        Reload conflicting fields
                    </Button>
                    <Button
                        type="button"
                        onClick={() => onKeepLocal(conflict.fence.token)}
                        aria-describedby={CONFLICT_FENCE_ID}
                        disabled={isResolving}
                    >
                        {deletesServerFile
                            ? "Keep local and delete server file"
                            : "Keep local and overwrite"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
